import React, { useEffect, useState } from "react";
import Title from "../../../components/Title";
import { pageHelp } from "../../../components/help/pageHelp";
import { Box, Alert, AlertTitle, Button, Typography } from "@mui/material";
import AdminCard from "./components/AdminCard";
import Back from "../../../components/Back";
import http from "../../../utils/http-common";
import { useNavigate } from "react-router-dom";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DnsIcon from "@mui/icons-material/Dns";
import GroupIcon from "@mui/icons-material/Group";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import MemoryIcon from "@mui/icons-material/Memory";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import UpdateIcon from "@mui/icons-material/Update";

type CardDef = { title: string; description: string; path: string; icon: React.ReactNode };
type Section = { heading: string; cards: CardDef[] };

const SECTIONS: Section[] = [
  {
    heading: "Infrastructure",
    cards: [
      { title: "Agent Management", description: "Approve, enable and inspect executor agents.", path: "/management/agents", icon: <MemoryIcon /> },
      { title: "Executor Procedures", description: "Configure procedures agents can run.", path: "/management/procedures", icon: <AccountTreeIcon /> },
      { title: "Instances", description: "Manage runtime instances and their configuration.", path: "/management/instances", icon: <DnsIcon /> },
      { title: "System Registry", description: "Frameworks, emulators and source registry.", path: "/management/system-registry", icon: <SettingsApplicationsIcon /> },
    ],
  },
  {
    heading: "Access & Users",
    cards: [
      { title: "User Management", description: "Manage users, status and group membership.", path: "/management/users", icon: <PeopleAltIcon /> },
      { title: "User Groups", description: "Groups, roles and permission sets.", path: "/management/groups", icon: <GroupIcon /> },
    ],
  },
  {
    heading: "Catalog & System",
    cards: [
      { title: "Products, Tags & Types", description: "Product taxonomy used to organise suites and cases.", path: "/management/products", icon: <LocalOfferIcon /> },
      { title: "Versions", description: "Component versions, updates and release notes.", path: "/management/versions", icon: <UpdateIcon /> },
    ],
  },
];

const Admin = () => {
  const navigate = useNavigate();
  const [pendingAgents, setPendingAgents] = useState(0);

  useEffect(() => {
    let active = true;
    http
      .get("/agent")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const n = list.filter((a: any) => a?.status === "pending").length;
        if (active) setPendingAgents(n);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <Title help={pageHelp.management.help} helpItems={pageHelp.management.items}>
        Management Overview
      </Title>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Administer agents, users, registry and the product catalog — and act on items that need attention.
      </Typography>

      {pendingAgents > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2, alignItems: "center" }}
          action={
            <Button color="warning" variant="contained" size="small" onClick={() => navigate("/management/agents")}>
              Review &amp; approve
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 700, mb: 0 }}>
            {pendingAgents} {pendingAgents === 1 ? "agent is" : "agents are"} awaiting approval
          </AlertTitle>
          New agents have registered and can&apos;t run cases until you approve them.
        </Alert>
      )}

      {SECTIONS.map((section, i) => (
        <Box key={section.heading} sx={{ mb: i === SECTIONS.length - 1 ? 1 : 3.5 }}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", fontWeight: 800, letterSpacing: 1, display: "block", mb: 1.25 }}
          >
            {section.heading}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: { xs: 1.5, sm: 2 },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            {section.cards.map((c) => (
              <AdminCard key={c.path} title={c.title} description={c.description} path={c.path} icon={c.icon} />
            ))}
          </Box>
        </Box>
      ))}

      <Back />
    </>
  );
};

export default Admin;
